/**
 * Unauthenticated REST client. The § 312k cancellation, the § 356a withdrawal and
 * the Art. 16 DSA notice work without an account, so they cannot go through `graphqlClient.ts`, which
 * always sends a Bearer token.
 *
 * The base URL is derived from `REACT_APP_GRAPHQL_ENDPOINT` because every
 * deployment already sets it; `REACT_APP_API_BASE_URL` overrides that.
 *
 * Nothing in here throws - these pages must never leave someone who wants out of
 * a contract on a blank screen, so every call resolves to a renderable result.
 */

/** API root, e.g. `https://api.lyra.rocks/api`. */
export const API_BASE_URL: string = (() => {
  const explicit = process.env.REACT_APP_API_BASE_URL;
  if (explicit) {
    return explicit.replace(/\/+$/, "");
  }

  const graphql =
    process.env.REACT_APP_GRAPHQL_ENDPOINT ||
    "http://localhost:5001/api/graphql";

  // ".../api/graphql" and ".../api/graphql-anonymous" both reduce to ".../api".
  return graphql.replace(/\/graphql(-anonymous)?\/*$/, "");
})();

/**
 * Why a call did not succeed. `rateLimited` is its own case because the limit is
 * the only abuse protection on the cancellation endpoint and will occasionally
 * hit a real customer, who has to be pointed at the other cancellation routes.
 */
export type ApiFailureReason =
  | "rateLimited"
  | "invalid"
  | "server"
  | "network";

export interface ApiFailure {
  ok: false;
  reason: ApiFailureReason;
  /** HTTP status, null when the request never reached the server. */
  status: number | null;
  /**
   * The error body, when the server sent a readable one. Both controllers answer
   * with a machine-readable reason; the wording belongs to this app.
   */
  body: ApiProblemBody | null;
}

/** The shape both controllers use for a rejected submission. */
export interface ApiProblemBody {
  /**
   * `CancellationInputProblem` or `CancellationOutcome` by name, or the
   * withdrawal's problem as withdrawalApi.ts normalises it.
   */
  problem?: string;
  /** The DSA controller's error code, e.g. "dsa_contact_details_required". */
  code?: string;
  /** The address the request can be sent to instead. */
  contactEmail?: string;
}

export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

/**
 * Reads the body of a response. An empty body is a success with nothing in it
 * (`POST /api/dsa/objections` answers 202 without one); anything unparseable is a
 * failure, because the cancellation result screen states a legal fact and must
 * not invent one from a response it could not read.
 */
const readBody = async (response: Response): Promise<unknown | undefined> => {
  let text: string;
  try {
    text = await response.text();
  } catch {
    return undefined;
  }

  if (text.trim().length === 0) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const failureReason = (status: number): ApiFailureReason => {
  if (status === 429) {
    return "rateLimited";
  }

  return status >= 400 && status < 500 ? "invalid" : "server";
};

const toFailure = (response: Response, body: unknown): ApiFailure => ({
  ok: false,
  reason: failureReason(response.status),
  status: response.status,
  body: (body as ApiProblemBody) || null,
});

/** POSTs JSON to the API and never rejects. */
export const postJson = async <T>(
  path: string,
  body: unknown,
): Promise<ApiResult<T>> => {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/${path.replace(/^\/+/, "")}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, reason: "network", status: null, body: null };
  }

  const payload = await readBody(response);

  if (!response.ok) {
    return toFailure(response, payload);
  }

  if (payload === undefined) {
    return { ok: false, reason: "server", status: response.status, body: null };
  }

  return { ok: true, data: payload as T };
};

/** GETs JSON from the API and never rejects. */
export const getJson = async <T>(path: string): Promise<ApiResult<T>> => {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/${path.replace(/^\/+/, "")}`);
  } catch {
    return { ok: false, reason: "network", status: null, body: null };
  }

  const payload = await readBody(response);

  if (!response.ok) {
    return toFailure(response, payload);
  }

  if (payload === undefined) {
    return { ok: false, reason: "server", status: response.status, body: null };
  }

  return { ok: true, data: payload as T };
};
